import styled from '@emotion/styled';
import {Container, media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';
import Link from 'next/link';

import {BlockIntro} from 'UI/components/layout/BlockIntro';
import {Card} from 'UI/components/layout/Card';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';

import {
    getDownloadAttribute,
    getHrefForLinkType,
    getRelAttributes,
    getTargetAttribute
} from 'Application/utils/linkFieldUtils';

import type {AboutUsBlockType, ModuleIntro} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `AboutUs` component.
 * This interface specifies all the required and optional props needed to render the AboutUs block component, which displays a grid of cards with images and optional links.
 * The interface ensures type safety and provides clear documentation for developers using this component in their applications.
 */
interface ComponentProps {
    /**
     * Array of content items containing image data, titles, and optional descriptions for grid display.
     * Each item represents a single grid cell with its associated visual and textual content that can optionally be rendered as a clickable link.
     * The cards support variable sizing through the isBig property and can include navigation links to internal or external resources.
     */
    cards: AboutUsBlockType['cards'];
    /**
     * Optional rich text description content displayed below the card grid.
     * This content provides additional context or detailed information about the cards shown in the grid layout.
     * When provided, it renders using the PayloadLexical component for rich text formatting capabilities.
     */
    description?: AboutUsBlockType['description'];
    /**
     * Unique identifier for the AboutUs block used for block intro functionality and component identification.
     * This identifier helps with content management and enables proper block-level configuration within the CMS system.
     * The ID is also used to associate the block with its corresponding intro content and styling.
     */
    id: string;
    /**
     * Module intro configuration object containing introductory content and settings for the block.
     * This object defines how the block introduction should be displayed above the card grid content, including titles, subtitles, and other introductory elements.
     * The intro provides context and sets expectations for the content that follows in the card grid layout.
     */
    intro: ModuleIntro;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing scenarios and automated test suites.
     * It enables reliable component selection in test environments for both unit and integration testing.
     *
     * @default 'AboutUs'
     */
    testId?: string;
}

/**
 * The AboutUs component renders a grid-based layout showcasing cards with images and optional links.
 * This component displays an introductory block followed by a responsive card grid where each card can contain an image and optionally function as a clickable link.
 * The component supports variable card sizes and includes additional descriptive content below the grid when provided.
 *
 * @param props             The component props.
 * @param props.cards       Array of content items containing image data, titles, and optional descriptions for grid display.
 * @param props.description Optional rich text description content displayed below the card grid.
 * @param props.id          Unique identifier for the image grid block used for block intro functionality and component identification.
 * @param props.intro       Module intro configuration object containing introductory content and settings for the block.
 * @param props.testId      A unique identifier, usually in the form of a string, assigned to the component for testing purposes.
 * @returns A React element representing the AboutUs component with block intro, card grid, and optional description.
 *
 * @example
 * ```tsx
 * <AboutUs
 *     cards={[
 *         {
 *             id: '1',
 *             image: imageData,
 *             hasLink: true,
 *             slideLink: { type: 'internal', reference: { value: 'page-id' } },
 *             isBig: false
 *         }
 *     ]}
 *     id="about-us-block"
 *     intro={{ title: 'About Us', subtitle: 'Learn more about our team' }}
 *     description={richTextContent}
 *     testId="AboutUs"
 * />
 * ```
 */
const AboutUs = observer(({cards, description, id, intro, testId = 'AboutUs'}: ComponentProps) => (
    <Container testId={testId}>
        <BlockIntro blockId={id} intro={intro} testId={`${testId}-Intro`} />
        <ViewportTrigger baseComponent={CardGrid}>
            {cards.map(card => {
                if (card.hasLink) {
                    const href = getHrefForLinkType(card.slideLink!);
                    const download = getDownloadAttribute(card.slideLink!);
                    const rel = getRelAttributes(card.slideLink!);
                    const target = getTargetAttribute(card.slideLink!);

                    return (
                        // eslint-disable-next-line @nfq/no-magic-numbers
                        <BigCard key={card.id ?? ''} span={card.isBig ? 2 : 1} constantSpan>
                            <ImageWrapper>
                                <StyledLink download={download} href={href} rel={rel} target={target}>
                                    <StyledImage image={card.image} />
                                </StyledLink>
                            </ImageWrapper>
                        </BigCard>
                    );
                }

                return (
                    // eslint-disable-next-line @nfq/no-magic-numbers
                    <Card key={card.id ?? ''} span={card.isBig ? 2 : 1} constantSpan>
                        <ImageWrapper>
                            <StyledImage image={card.image} />
                        </ImageWrapper>
                    </Card>
                );
            })}
        </ViewportTrigger>
        <Spacer y={20} />
        <ViewportTrigger baseComponent="div">
            <motion.div variants={FadeInAnimation}>
                {description && <PayloadLexical description={description} />}
            </motion.div>
        </ViewportTrigger>
    </Container>
));

AboutUs.displayName = 'AboutUs';

export {AboutUs};

const CardGrid = styled.div`
    display: grid;
    gap: ${spacing(6)};
    grid-auto-flow: row dense;
    grid-auto-rows: auto;
    grid-template-columns: 1fr 1fr;

    ${media('sm')} {
        grid-auto-rows: 23.6rem;
        grid-template-columns: 1fr 1fr;
    }

    ${media('lg')} {
        grid-auto-rows: 23.6rem;
        grid-template-columns: 1fr 1fr 1fr;
    }
`;

const BigCard = styled(Card)`
    grid-column: span 2;

    ${media('sm')} {
        grid-column: span 1;
    }
`;

const ImageWrapper = styled.div`
    align-self: center;
    display: grid;
    grid-row: 1 / -1;
    height: 100%;
    justify-self: center;
    place-items: center;
`;

const StyledLink = styled(Link)`
    align-items: center;
    display: flex;
    height: 100%;
    max-height: 100%;
    min-height: 0;

    &:focus-visible {
        outline: 2px auto ${({theme}) => theme.colors.tertiary};
        outline-offset: 2px;
    }
`;

const StyledImage = styled(PayloadImage)`
    height: 100%;
    max-height: 100%;
    max-width: 100%;
    min-height: 0;
    min-width: 0;
    object-position: center center;
    width: 100%;

    picture:has(&) {
        height: 100%;
        width: 100%;
    }
`;